import {
    RecurringTransferEndType as RecurringTransferEndTypeEnum,
    type RecurringTransferFrequency as RecurringTransferFrequencyEnum,
    type RecurringTransferSetupDetails,
} from '@fruitful/types';
import {
    BackButton,
    BackButtonIconType,
    isNonMobileWeb,
    Sheet,
    View,
} from 'smoothie';

import RecurringTransferDate from './RecurringTransferDate';
import RecurringTransferDescription from './RecurringTransferDescription';
import RecurringTransferEndCount from './RecurringTransferEndCount';
import RecurringTransferEndType from './RecurringTransferEndType';
import RecurringTransferFrequency from './RecurringTransferFrequency';

export enum RecurringTransferSetupStep {
    DESCRIPTION = 'description',
    FREQUENCY = 'frequency',
    START = 'start',
    END_TYPE = 'endType',
    END_COUNT = 'endCount',
}

const STEP_BACK_MAP: Record<
    RecurringTransferSetupStep,
    RecurringTransferSetupStep | null
> = {
    [RecurringTransferSetupStep.DESCRIPTION]: null,
    [RecurringTransferSetupStep.FREQUENCY]:
        RecurringTransferSetupStep.DESCRIPTION,
    [RecurringTransferSetupStep.START]: RecurringTransferSetupStep.FREQUENCY,
    [RecurringTransferSetupStep.END_TYPE]: RecurringTransferSetupStep.START,
    [RecurringTransferSetupStep.END_COUNT]: RecurringTransferSetupStep.END_TYPE,
};

interface RecurringTransferSetupSheetProps {
    isOpen: boolean;
    details: RecurringTransferSetupDetails;
    onUpdateDetails: (details: RecurringTransferSetupDetails) => void;
    onClose: () => void;
    step: RecurringTransferSetupStep;
    onGoToStep: (step: RecurringTransferSetupStep) => void;
    onOpenStartDateSheet: () => void;
    onOpenEndDateSheet?: () => void;
}

const RecurringTransferSetupSheet = ({
    isOpen,
    details,
    onUpdateDetails,
    onClose,
    step,
    onGoToStep,
    onOpenStartDateSheet,
    onOpenEndDateSheet,
}: RecurringTransferSetupSheetProps) => {
    let title = '';
    let content: React.ReactNode = null;

    switch (step) {
        case RecurringTransferSetupStep.DESCRIPTION:
            title = 'Description';
            content = (
                <RecurringTransferDescription
                    description={details.description}
                    onSubmit={(description?: string) => {
                        onUpdateDetails({ ...details, description });
                        onGoToStep(RecurringTransferSetupStep.FREQUENCY);
                    }}
                />
            );
            break;
        case RecurringTransferSetupStep.FREQUENCY:
            title = 'Repeats';
            content = (
                <RecurringTransferFrequency
                    frequency={details.frequency}
                    onSubmit={(frequency?: RecurringTransferFrequencyEnum) => {
                        onUpdateDetails({ ...details, frequency });
                        onGoToStep(RecurringTransferSetupStep.START);
                    }}
                />
            );
            break;
        case RecurringTransferSetupStep.START:
            title = 'Starts';
            content = (
                <RecurringTransferDate
                    date={details.start}
                    onOpenStartDateSelector={onOpenStartDateSheet}
                    onSubmit={(start?: Date) => {
                        onUpdateDetails({ ...details, start });
                        onGoToStep(RecurringTransferSetupStep.END_TYPE);
                    }}
                />
            );
            break;
        case RecurringTransferSetupStep.END_TYPE:
            title = 'Ends';
            content = (
                <RecurringTransferEndType
                    endType={details.end?.type}
                    onSubmit={(type?: RecurringTransferEndTypeEnum) => {
                        onUpdateDetails({
                            ...details,
                            end: { ...details.end, type },
                        });

                        if (type === RecurringTransferEndTypeEnum.ON) {
                            onOpenEndDateSheet?.();
                            onClose();
                        } else if (
                            type === RecurringTransferEndTypeEnum.AFTER
                        ) {
                            onGoToStep(RecurringTransferSetupStep.END_COUNT);
                        } else {
                            // If value is never, set up is done
                            onClose();
                        }
                    }}
                />
            );
            break;
        case RecurringTransferSetupStep.END_COUNT:
            title = 'Ends after';
            content = (
                <RecurringTransferEndCount
                    endCount={details.end?.count}
                    onSubmit={(count?: number) => {
                        onUpdateDetails({
                            ...details,
                            end: { type: details.end?.type, count },
                        });
                        onClose();
                    }}
                />
            );
    }

    return (
        <Sheet
            isOpen={isOpen}
            forceKeyboardCompatibleBottomSheet={true}
            title={title}
            showCloseButton={true}
            onClose={onClose}
            headerLeftAction={
                STEP_BACK_MAP[step] == null || isNonMobileWeb ? undefined : (
                    <BackButton
                        iconType={BackButtonIconType.ARROW_LEFT}
                        customBackFn={() => {
                            onGoToStep(
                                STEP_BACK_MAP[
                                    step
                                ] as RecurringTransferSetupStep,
                            );
                        }}
                    />
                )
            }
        >
            <View width="100%" marginTop={24} marginBottom={16}>
                {content}
            </View>
        </Sheet>
    );
};

export default RecurringTransferSetupSheet;
